import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import axios from 'axios'
import V2Layout from '../components/V2Layout'
import SEO from '../components/SEO'
import '../versions/v2/capabilities.css'

const API = ''

const VALVE_TYPES = [
    { name: 'Check Valve', func: 'Prevents backflow in propulsion and pressurization lines' },
    { name: 'Relief Valve', func: 'Protects systems from overpressure events' },
    { name: 'Ball Valve', func: 'On/off flow isolation for ground support and vehicle systems' },
    { name: 'Regulators', func: 'Precise outlet pressure regulation across a range of inlet conditions' },
]

const FAQS = [
    {
        q: 'What line sizes and pressure ratings do PFC aerospace valves support?',
        a: 'PFC valves are custom engineered or adapted from proven designs in line sizes from ¼" to 8", with pressure ratings up to 15,000 PSIG.'
    },
    {
        q: 'Are PFC valves qualified for cryogenic service?',
        a: 'Yes. PFC has produced cryogenic butterfly valves qualified for liquid oxygen, methane, and hydrogen service, as well as ambient-to-cryo temperature swings.'
    },
    {
        q: 'What certifications does PFC manufacturing hold?',
        a: "PFC production runs under an AS9100D-certified quality system, with cleaning, passivation, assembly, and environmental test performed in-house at PFC's Lincoln, California facility."
    },
    {
        q: 'Can PFC design a custom valve for a non-standard application?',
        a: 'Yes. PFC can modify an existing proven design or develop a new design from scratch to meet a customer drawing or specification.'
    },
    {
        q: 'Where are PFC aerospace valves manufactured and tested?',
        a: "All design, manufacturing, cleaning, assembly, and functional test happen in-house at PFC's Lincoln, California facility."
    },
    {
        q: 'What programs use PFC aerospace valves?',
        a: 'PFC valves fly on commercial launch vehicles and propulsion systems, and support ground operations at Kennedy Space Center, Stennis Space Center, Marshall Space Flight Center, Vandenberg AFB, Edwards AFB, and Arnold AFB, plus aviation, defense, maritime, and medical device applications.'
    },
]

const AerospaceValvesPage = () => {
    const [categories, setCategories] = useState([])
    const [products, setProducts] = useState([])

    useEffect(() => {
        axios.get(`${API}/api/categories`).then(r => setCategories(Array.isArray(r.data) ? r.data : [])).catch(() => { })
        axios.get(`${API}/api/products`).then(r => setProducts(Array.isArray(r.data) ? r.data.slice(0, 6) : [])).catch(() => { })
    }, [])

    const categoryLink = (name) => {
        const match = categories.find(c => c.name.toLowerCase() === name.toLowerCase())
        return match ? `/products/category/${match.id}` : '/products/category/all'
    }

    const faqJsonLd = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS.map(f => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a }
        }))
    }

    return (
        <V2Layout pageClass="pfc-capabilities">
            <SEO
                title="Aerospace Valves – Types, Applications & Certifications"
                description="Space-rated check valves, relief valves, ball valves, regulators, and cryogenic valves engineered for launch vehicle, ground support, and defense programs. AS9100D-certified manufacturing in Lincoln, CA."
            />
            <Helmet>
                <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
            </Helmet>

            <section className="pfc-page-hero">
                <div className="pfc-page-hero-inner">
                    <p className="v2-section-label">Aerospace Valves</p>
                    <h1 className="pfc-page-hero-title">Aerospace Valves & Pressure Controls</h1>
                    <p className="pfc-page-hero-sub">
                        Space-rated valve types, program applications, and the certifications behind every
                        flight-critical component PFC builds.
                    </p>
                </div>
            </section>

            <section className="v2-content" style={{ padding: '4rem 0 6rem' }}>
                <div className="pfc-article">
                    <h2>Space-Rated Valve Types Engineered for Extreme Environments</h2>
                    <p>
                        Precision Fluid Controls designs and manufactures check valves, relief valves, ball valves,
                        and pressure regulators built to hold tight tolerances under the pressure, temperature, and
                        vibration extremes of spaceflight. Every valve is custom engineered or adapted from a proven
                        design to match line sizes from ¼" to 8" and pressure ratings up to 15,000 PSIG.
                    </p>

                    <h3>Check Valves &amp; Relief Valves</h3>
                    <p>
                        Check valves and relief valves protect propulsion and pressurization systems from backflow
                        and overpressure events, with cracking pressures and leakage rates verified through
                        in-house functional test.
                    </p>

                    <h3>Ball Valves &amp; Regulators</h3>
                    <p>
                        Ball valves provide reliable on/off flow control for ground support and vehicle systems,
                        while dome-loaded and multi-stage regulators deliver precise outlet pressure regulation
                        across a wide range of inlet conditions.
                    </p>

                    <h3>Cryogenic Valves</h3>
                    <p>
                        Since establishing cryogenic butterfly valve production, PFC has supplied valves qualified
                        for cryogenic propellants and ambient-to-cryo temperature swings, supporting propulsion
                        systems from liquid oxygen and methane to hydrogen.
                    </p>

                    <h2>Valve Types at a Glance</h2>
                    <div style={{ overflowX: 'auto' }}>
                        <table className="pfc-spec-table">
                            <thead>
                                <tr>
                                    <th>Valve Type</th>
                                    <th>Function</th>
                                    <th>Line Size / Pressure</th>
                                    <th>Browse</th>
                                </tr>
                            </thead>
                            <tbody>
                                {VALVE_TYPES.map(v => (
                                    <tr key={v.name}>
                                        <td>{v.name}</td>
                                        <td>{v.func}</td>
                                        <td>¼&quot; – 8&quot; · up to 15,000 PSIG</td>
                                        <td><Link to={categoryLink(v.name)}>View products →</Link></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {products.length > 0 && (
                        <>
                            <h2>Featured Aerospace Valve Products</h2>
                            <p>
                                Browse individual product pages for full specifications and datasheets where available:
                            </p>
                            <ul>
                                {products.map(p => (
                                    <li key={p.id}><Link to={`/products/${p.id}`}>{p.name}</Link></li>
                                ))}
                            </ul>
                        </>
                    )}

                    <h2>Applications Across Launch Vehicle &amp; Ground Support Programs</h2>
                    <p>
                        PFC valves and pressure controls fly on launch vehicles and propulsion systems, and operate
                        in ground support equipment at Kennedy Space Center, Stennis Space Center, Marshall Space
                        Flight Center, Vandenberg AFB, Edwards AFB, Arnold AFB, and NASA facilities nationwide. The
                        same design and manufacturing capability serves aviation, defense, maritime submarine, and
                        medical device applications that demand the same flight-critical reliability.
                    </p>

                    <h2>AS9100D-Certified Manufacturing &amp; Quality</h2>
                    <p>
                        All aerospace valve production runs under an AS9100D-certified quality system, with
                        cleaning, passivation, assembly, and environmental test performed in-house at PFC's
                        Lincoln, California facility. Every valve is functionally tested and traceable from raw
                        material through final inspection before it ships.
                    </p>

                    <h2>Frequently Asked Questions</h2>
                    {FAQS.map(f => (
                        <div key={f.q}>
                            <h3>{f.q}</h3>
                            <p>{f.a}</p>
                        </div>
                    ))}

                    <p>
                        Explore our <Link to="/products">product catalog</Link> or{' '}
                        <Link to="/contactus">contact our engineering team</Link> to discuss a valve design for
                        your program.
                    </p>
                </div>
            </section>
        </V2Layout>
    )
}

export default AerospaceValvesPage
